import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { GraduationCap, ShieldCheck, ArrowLeft } from "@phosphor-icons/react";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const email = params.get("email") || "";
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) toast.error("Token de redefinição ausente.");
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    if (pw1.length < 6) { toast.error("Senha precisa ter no mínimo 6 caracteres."); return; }
    if (pw1 !== pw2) { toast.error("As senhas não conferem."); return; }
    setLoading(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, new_password: pw1 });
      toast.success("Senha redefinida! Faça login com a nova senha.");
      nav("/login");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erro");
    } finally { setLoading(false); }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-[#F7F3EE] relative overflow-hidden">
      <div className="blob w-[500px] h-[500px] bg-[#FFB86B]/60 -top-40 -left-40" />
      <div className="blob w-[400px] h-[400px] bg-[#1E5FFF]/30 bottom-0 right-0" />

      <div className="w-full max-w-md card-elevated rounded-3xl p-6 sm:p-8 relative z-10">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-10 h-10 rounded-2xl gradient-primary flex items-center justify-center shadow-lg">
            <GraduationCap size={22} color="white" weight="duotone" />
          </div>
          <div>
            <div className="font-heading font-black text-base leading-none">Caminhos</div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">APP</div>
          </div>
        </div>

        <div className="pill-badge bg-[#E0EBFF] text-[#1E5FFF] mb-4"><ShieldCheck size={11} weight="fill" /> Nova senha</div>
        <h1 className="font-heading text-3xl font-black text-[#12143A] leading-tight">Criar nova senha</h1>
        {email && <p className="text-sm text-zinc-600 mt-2">Para a conta <b>{email}</b>.</p>}

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Nova senha</Label>
            <Input data-testid="reset-pw1" type="password" value={pw1} required minLength={6}
                   onChange={e => setPw1(e.target.value)} placeholder="Mínimo 6 caracteres" autoComplete="new-password"
                   className="mt-2 h-14 rounded-2xl border-zinc-200 focus:border-[#1E5FFF] focus-visible:ring-[#1E5FFF]/20 text-base" />
          </div>
          <div>
            <Label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Confirme a senha</Label>
            <Input data-testid="reset-pw2" type="password" value={pw2} required minLength={6}
                   onChange={e => setPw2(e.target.value)} placeholder="Repita a nova senha" autoComplete="new-password"
                   className="mt-2 h-14 rounded-2xl border-zinc-200 focus:border-[#1E5FFF] focus-visible:ring-[#1E5FFF]/20 text-base" />
          </div>
          <Button data-testid="reset-submit" type="submit" disabled={loading || !token}
                  className="w-full h-14 rounded-2xl gradient-primary hover:opacity-90 text-white font-bold shadow-[0_10px_30px_rgba(30,95,255,0.35)]">
            {loading ? "Salvando…" : "Salvar nova senha"}
          </Button>
          <Link to="/login" className="mt-2 inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-[#1E5FFF]">
            <ArrowLeft size={14} /> Voltar para o login
          </Link>
        </form>
      </div>
    </div>
  );
}
